import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

import { Autocomplete, Box, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import { type AddressSuggestion, getAddressSuggestions } from '~/services/LuxLoyalty';

export interface BusinessAddress {
  unit_number: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  formatted_address: string | null;
}

interface BusinessAddressAutocompleteProps {
  value: BusinessAddress | null;
  onChange: (address: BusinessAddress | null) => void;
  disabled?: boolean;
}

const EMPTY_ADDRESS: BusinessAddress = {
  unit_number: null,
  street: null,
  city: null,
  state: null,
  postcode: null,
  country: null,
  formatted_address: null,
};

export default function BusinessAddressAutocomplete({
  value,
  onChange,
  disabled = false,
}: BusinessAddressAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState<AddressSuggestion | null>(null);

  // Initialize input value from the formatted address
  useEffect(() => {
    if (value?.formatted_address && !selectedSuggestion) {
      setInputValue(value.formatted_address);
    }
  }, [value?.formatted_address, selectedSuggestion]);

  // Debounce the input for API calls
  const debouncedSetInput = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedInput(value);
      }, 300),
    [setDebouncedInput],
  );

  useEffect(() => {
    return () => {
      debouncedSetInput.cancel();
    };
  }, [debouncedSetInput]);

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: 'input' | 'reset' | 'clear') => {
      setInputValue(newInputValue);
      if (reason === 'input') {
        debouncedSetInput(newInputValue);
        return;
      }
      if (reason === 'clear') {
        setDebouncedInput('');
      }
    },
    [debouncedSetInput, setDebouncedInput],
  );

  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ['address-suggestions', debouncedInput],
    queryFn: async ({ signal }) => {
      const response = await getAddressSuggestions(debouncedInput, { signal });
      return response.result || [];
    },
    enabled: debouncedInput.length >= 3,
    staleTime: 60000,
  });

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: AddressSuggestion | null) => {
      setSelectedSuggestion(newValue);

      if (newValue) {
        setInputValue(newValue.display.formatted_address || '');
        setDebouncedInput('');
        const newAddress: BusinessAddress = {
          unit_number: newValue.address_components.unit_number || null,
          street: newValue.address_components.street || null,
          city: newValue.address_components.city || null,
          state: newValue.address_components.state || null,
          postcode: newValue.address_components.postcode || null,
          country: newValue.address_components.country || null,
          formatted_address: newValue.display.formatted_address || null,
        };
        onChange(newAddress);
      } else {
        setInputValue('');
        setDebouncedInput('');
        onChange(null);
      }
    },
    [onChange, setDebouncedInput, setInputValue],
  );

  const handleManualChange = useCallback(
    (field: keyof BusinessAddress, fieldValue: string) => {
      const updatedAddress = {
        ...(value || EMPTY_ADDRESS),
        [field]: fieldValue || null,
      };

      // Update formatted_address when manual fields change
      const parts = [
        updatedAddress.unit_number,
        updatedAddress.street,
        updatedAddress.city,
        updatedAddress.state,
        updatedAddress.postcode,
        updatedAddress.country,
      ].filter(Boolean);

      updatedAddress.formatted_address = parts.length > 0 ? parts.join(', ') : null;

      onChange(updatedAddress);
    },
    [onChange, value],
  );

  return (
    <Box>
      <Autocomplete
        freeSolo
        options={suggestions}
        filterOptions={(x) => x} // Don't filter - we use server-side filtering
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.display.formatted_address;
        }}
        isOptionEqualToValue={(option, val) => {
          if (typeof val === 'string') {
            return option.display.formatted_address === val;
          }
          return option.display.addressId === val.display.addressId;
        }}
        renderOption={({ key, ...props }, option) => (
          <li key={key ?? option.display.addressId} {...props}>
            <Box>
              <Typography variant="body1">{option.display.mainText}</Typography>
              <Typography variant="body2" color="text.secondary">
                {option.display.secondaryText}
              </Typography>
            </Box>
          </li>
        )}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        loading={isLoading}
        disabled={disabled}
        slotProps={{
          popper: {
            sx: { zIndex: 1500 }, // Ensure dropdown is above other elements
          },
          paper: {
            elevation: 8,
          },
        }}
        PaperComponent={(props) => <Paper {...props} elevation={8} />}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Business Address"
            placeholder="Start typing to search..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={debouncedInput.length < 3 ? 'Type at least 3 characters to search' : 'No addresses found'}
        sx={{ mb: 2 }}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            fullWidth
            label="Unit Number"
            value={value?.unit_number || ''}
            onChange={(e) => handleManualChange('unit_number', e.target.value)}
            disabled={disabled}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 9 }}>
          <TextField
            fullWidth
            label="Street"
            value={value?.street || ''}
            onChange={(e) => handleManualChange('street', e.target.value)}
            disabled={disabled}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextField
            fullWidth
            label="City"
            value={value?.city || ''}
            onChange={(e) => handleManualChange('city', e.target.value)}
            disabled={disabled}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextField
            fullWidth
            label="State"
            value={value?.state || ''}
            onChange={(e) => handleManualChange('state', e.target.value)}
            disabled={disabled}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextField
            fullWidth
            label="Postcode"
            value={value?.postcode || ''}
            onChange={(e) => handleManualChange('postcode', e.target.value)}
            disabled={disabled}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextField
            fullWidth
            label="Country"
            value={value?.country || ''}
            onChange={(e) => handleManualChange('country', e.target.value)}
            disabled={disabled}
            size="small"
          />
        </Grid>
      </Grid>
    </Box>
  );
}
